'use client';

import { useState, useEffect, useCallback } from 'react';
import { clientTransactionsApi } from '../api';
import { useLiveSync } from '@/lib/live-sync';
import type { ClientTransaction, FetchTransactionsParams } from '../types';

export function useTransactions(tenantSlug: string, initialParams: FetchTransactionsParams = {}) {
  const [transactions, setTransactions] = useState<ClientTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Manage all search/filter/pagination parameters in local state
  const [params, setParams] = useState<FetchTransactionsParams>({
    limit: 10,
    offset: 0,
    sortBy: 'created_at',
    sortOrder: 'DESC',
    ...initialParams,
  });

  const fetchTransactions = useCallback(async () => {
    if (!tenantSlug) return;

    try {
      setError(null);
      const result = await clientTransactionsApi.getClientTransactions(tenantSlug, params);
      setTransactions(result.transactions);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch transactions'));
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug, params]);

  useLiveSync({
    onSync: fetchTransactions,
    scope: ['transactions', 'wallet'],
    intervalMs: 20000,
    enabled: !!tenantSlug,
  });

  useEffect(() => {
    setIsLoading(true);
    fetchTransactions();
  }, [fetchTransactions]);

  const updateParams = useCallback((newParams: Partial<FetchTransactionsParams>) => {
    setParams((prev) => {
      const updated = { ...prev, ...newParams };
      // If any filter/search changes, reset pagination to the first page (offset 0)
      if (
        newParams.search !== undefined ||
        newParams.type !== undefined ||
        newParams.status !== undefined ||
        newParams.currency !== undefined ||
        newParams.method !== undefined ||
        newParams.startDate !== undefined ||
        newParams.endDate !== undefined
      ) {
        updated.offset = 0;
      }
      return updated;
    });
  }, []);

  const handlePageChange = useCallback((newOffset: number) => {
    updateParams({ offset: newOffset });
  }, [updateParams]);

  return {
    transactions,
    total,
    isLoading,
    error,
    params,
    updateParams,
    handlePageChange,
    refetch: fetchTransactions,
  };
}
